import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PracticeSetupWizardStore,
  SetupWizardStepsComponent,
} from '@principle-theorem/ng-principle';
import {
  CurrentPracticeScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { SetupEngagementTracker } from '@principle-theorem/principle-core';
import {
  PracticeSetupStepCategory,
  PracticeSetupWizardStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  HISTORY_DATE_FORMAT,
} from '@principle-theorem/shared';
import { take } from 'rxjs/operators';

export const SETUP_WIZARD_PAGE_IMPORTS = [
  NgCommonModule,
  MatProgressBarModule,
  MatButtonModule,
  MatIconModule,
  MatProgressSpinnerModule,
  SetupWizardStepsComponent,
];

@Component({
  selector: 'pr-setup-wizard-page',
  templateUrl: './setup-wizard-page.component.html',
  styleUrls: ['./setup-wizard-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: SETUP_WIZARD_PAGE_IMPORTS,
})
export class SetupWizardPageComponent {
  private _destroyRef = inject(DestroyRef);
  private _currentPracticeScope = inject(CurrentPracticeScope);
  private _organisationService = inject(OrganisationService);
  private _panelElements = viewChildren<
    MatExpansionPanel,
    ElementRef<HTMLElement>
  >(MatExpansionPanel, {
    read: ElementRef,
  });
  private _storageKey: string | undefined;
  private _hasResolvedExpandedCategories = signal(false);
  private _hasStorageKey = signal(false);
  private _shouldScrollOnRestore = signal(false);
  private readonly _currentStaffer = toSignal(
    this._organisationService.staffer$,
    { initialValue: undefined }
  );

  readonly dateFormat = HISTORY_DATE_FORMAT;
  store = inject(PracticeSetupWizardStore);

  readonly expandedCategories = signal<Set<PracticeSetupStepCategory>>(
    new Set()
  );

  readonly targetLaunchDate = computed(() => {
    const wizard = this.store.wizard();
    return wizard?.targetLaunchDate;
  });

  readonly isWizardComplete = computed(() => {
    const wizard = this.store.wizard();
    return (
      wizard?.status === PracticeSetupWizardStatus.Complete &&
      !wizard.dismissedAt
    );
  });

  constructor() {
    this._initializeStore();
    this._restoreOrInitializeExpandedCategories();

    effect(() => {
      if (this.isWizardComplete()) {
        this.expandedCategories.set(new Set());
        this._hasResolvedExpandedCategories.set(true);
      }
    });

    effect(() => {
      const sections = this.store.categories();
      if (
        !this._hasStorageKey() ||
        this._hasResolvedExpandedCategories() ||
        this.isWizardComplete() ||
        sections.length === 0
      ) {
        return;
      }

      this._initializeExpandedCategories();
      this._hasResolvedExpandedCategories.set(true);
      this._shouldScrollOnRestore.set(true);
    });

    effect(() => {
      const panels = this._panelElements();
      const shouldScroll = this._shouldScrollOnRestore();
      if (!shouldScroll || panels.length === 0) {
        return;
      }
      this._shouldScrollOnRestore.set(false);
      setTimeout(() => this._scrollToFirstExpanded());
    });

    effect(() => {
      const wizard = this.store.wizard();
      const staffer = this._currentStaffer();

      if (!wizard?.ref || !staffer?.ref) {
        return;
      }

      void SetupEngagementTracker.recordEngagement(wizard.ref, staffer.ref);
    });
  }

  isExpanded(category: PracticeSetupStepCategory): boolean {
    return this.expandedCategories().has(category);
  }

  onPanelOpened(category: PracticeSetupStepCategory): void {
    const expanded = new Set(this.expandedCategories());
    expanded.add(category);
    this.expandedCategories.set(expanded);
    this._persistExpandedCategories();
  }

  onPanelClosed(category: PracticeSetupStepCategory): void {
    const expanded = new Set(this.expandedCategories());
    expanded.delete(category);
    this.expandedCategories.set(expanded);
    this._persistExpandedCategories();
  }

  retry(): void {
    this._initializeStore();
  }

  protected _initializeExpandedCategories(): void {
    if (this.isWizardComplete()) {
      this.expandedCategories.set(new Set());
      return;
    }

    const sections = this.store.categories();
    if (sections.length === 0) {
      return;
    }

    const expanded = new Set<PracticeSetupStepCategory>();
    const firstUnstartedSection = sections.find(
      (section) => section.completed === 0
    );

    for (const section of sections) {
      if (section.completed === section.total) {
        continue;
      }

      if (section.completed > 0) {
        expanded.add(section.category);
        continue;
      }

      if (firstUnstartedSection?.category === section.category) {
        expanded.add(section.category);
      }
    }

    this.expandedCategories.set(expanded);
  }

  protected _initializeStore(): void {
    this.store.loadWizard(
      this._currentPracticeScope.doc$.pipe(
        filterUndefined(),
        take(1),
        takeUntilDestroyed(this._destroyRef)
      )
    );
  }

  private _restoreOrInitializeExpandedCategories(): void {
    this._currentPracticeScope.doc$
      .pipe(filterUndefined(), take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((practice) => {
        this._storageKey = `setupWizard:expandedCategories:${practice.ref.path}`;
        this._hasStorageKey.set(true);

        if (this.isWizardComplete()) {
          this.expandedCategories.set(new Set());
          this._hasResolvedExpandedCategories.set(true);
          return;
        }

        const stored = localStorage.getItem(this._storageKey);
        if (stored) {
          try {
            const categories = JSON.parse(
              stored
            ) as PracticeSetupStepCategory[];
            const availableCategories = this.store
              .categories()
              .map((section) => section.category);
            const validCategories = categories.filter((category) =>
              availableCategories.includes(category)
            );
            if (validCategories.length > 0) {
              this.expandedCategories.set(new Set(validCategories));
              this._hasResolvedExpandedCategories.set(true);
              this._shouldScrollOnRestore.set(true);
            }
          } catch {
            return;
          }
        }
      });
  }

  private _persistExpandedCategories(): void {
    if (!this._storageKey) {
      return;
    }
    const expanded = [...this.expandedCategories()];
    localStorage.setItem(this._storageKey, JSON.stringify(expanded));
  }

  private _scrollToFirstExpanded(): void {
    const panelElements = this._panelElements();
    const firstExpandedIndex = this.store
      .categories()
      .findIndex((section) => this.expandedCategories().has(section.category));
    if (firstExpandedIndex >= 0 && panelElements[firstExpandedIndex]) {
      panelElements[firstExpandedIndex].nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }
}
